import type {
  SellerDetail,
  HostDetail,
  SalesAgentDetailAdmin,
  AdminUserListRow,
  StaffPasswordResetEmailStatus,
} from "@/services/adminService";
import type {
  AdminPasswordResetEmail,
  AdminUserDetail,
  AdminUserListItem,
  AdminUserRole,
} from "./types";

const API_ROLE_TO_ADMIN_USER_ROLE: Record<
  AdminUserListRow["role"],
  AdminUserRole
> = {
  SELLER: "seller",
  HOST: "host",
  SALES_AGENT: "sales-agent",
  ADMIN: "admin",
};

export function adminUserRowToListItem(row: AdminUserListRow): AdminUserListItem {
  return {
    userId: row.user_id,
    role: API_ROLE_TO_ADMIN_USER_ROLE[row.role],
    name: row.name,
    accountEmail: row.account_email,
    contactPhone: row.contact_phone,
    isActive: row.is_active,
    walletBalance: row.wallet_balance,
    relevantCount: row.relevant_count,
    regionCode: row.region_code,
    isSuperAdmin: row.is_super_admin === true,
  };
}

function toAdminPasswordResetEmail(
  status: StaffPasswordResetEmailStatus,
): AdminPasswordResetEmail {
  return {
    canSend: status.can_send,
    blockedReason: status.blocked_reason,
    activeLinkExpiresAt: status.active_link_expires_at,
    nextAllowedAt: status.next_allowed_at,
  };
}

export function sellerDetailToAdmin(row: SellerDetail): AdminUserDetail {
  return {
    userId: row.user_id,
    name: row.name,
    role: "seller",
    accountEmail: row.account_email,
    contactPhone: row.contact_phone,
    contactEmail: row.contact_email,
    vatNumber: row.vat_number,
    billingAddress: row.billing_address,
    isActive: row.is_active,
    walletBalance: row.wallet_balance,
    totalRevenue: row.total_revenue,
    relevantCount: 0,
    lastLoginAt: row.last_login_at,
    passwordResetEmail: toAdminPasswordResetEmail(row.password_reset_email),
  };
}

export function hostDetailToAdmin(
  row: HostDetail,
): AdminUserDetail {
  return {
    userId: row.user_id,
    name: row.name,
    role: "host",
    accountEmail: row.account_email,
    contactPhone: row.contact_phone,
    contactEmail: row.contact_email,
    vatNumber: row.vat_number,
    billingAddress: row.billing_address,
    isActive: row.is_active,
    walletBalance: row.wallet_balance,
    totalRevenue: row.total_revenue,
    relevantCount: row.structures_count,
    lastLoginAt: row.last_login_at,
    passwordResetEmail: toAdminPasswordResetEmail(row.password_reset_email),
  };
}

export function salesAgentDetailToAdmin(
  row: SalesAgentDetailAdmin,
): AdminUserDetail {
  return {
    userId: row.user_id,
    name: row.name,
    role: "sales-agent",
    accountEmail: row.email,
    contactPhone: row.contact_phone,
    contactEmail: row.contact_email,
    vatNumber: row.vat_number,
    billingAddress: null,
    isActive: row.is_active,
    walletBalance: row.wallet_balance,
    totalRevenue: row.total_revenue,
    relevantCount: row.assigned_scopes_count,
    lastLoginAt: row.last_login_at,
    passwordResetEmail: toAdminPasswordResetEmail(row.password_reset_email),
  };
}
